import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import {
  useRouter,
  useParams,
  useLocation,
  Link,
  type ActiveLinkOptions,
} from "@tanstack/react-router";
import ListGroupItem from "react-bootstrap/ListGroupItem";
import { joinClasses } from "@/utils/styling";
import styles from "./SidebarItem.module.css";

import { FiChevronDown, FiChevronRight, FiExternalLink } from "react-icons/fi";

export type SidebarItemProps = {
  icon?: ReactNode;
  text: ReactNode;
  linkTo?: string; // Should have a linkTo if and only if does not have subItems
  hasPermission?: boolean;
  subItems?: SubMenuItemProps[];
};

export type SubMenuItemProps = {
  text: string;
  linkTo?: string;
  title?: string;
  isExternal?: boolean;
  hasPermission?: boolean;
  onClick?: () => void; // Item should have linkTo OR onClick, not both
};

const topLinkClasses = "d-flex justify-content-between px-3 py-3";

const activeLinkOptions: ActiveLinkOptions = {
  activeProps: {
    className: styles.active,
  },
  activeOptions: {
    exact: true,
  },
};

export default function SidebarItem({
  icon = null,
  text,
  linkTo = "",
  hasPermission = true,
  subItems = [],
}: SidebarItemProps) {
  const router = useRouter();
  const params = useParams({ from: "/$leaid" });
  const location = useLocation();
  const currentPath = location.pathname;

  const [areSubLinksOpen, setAreSubLinksOpen] = useState(false);

  const hasSubLinks = subItems.length > 0;

  const parsedSubPaths = useMemo(() => {
    return subItems
      .filter((item) => !item.isExternal && !!item.linkTo)
      .map(
        (item) =>
          router.buildLocation({
            to: item.linkTo,
            params: { leaid: params.leaid },
          })?.pathname
      );
  }, [subItems, router, params]);

  const isSubPageActive = useMemo(
    () =>
      hasSubLinks &&
      parsedSubPaths.some((path) => path && currentPath.startsWith(path)),
    [hasSubLinks, parsedSubPaths, currentPath]
  );

  const wasSubPageActiveRef = useRef(isSubPageActive);

  useEffect(() => {
    const wasActive = wasSubPageActiveRef.current;

    if (wasActive && !isSubPageActive && areSubLinksOpen) {
      setAreSubLinksOpen(false);
    }

    wasSubPageActiveRef.current = isSubPageActive;
  }, [isSubPageActive, areSubLinksOpen]);

  const handleTopItemClick = () => {
    setAreSubLinksOpen((prev) => !prev);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    if (hasSubLinks && areSubLinksOpen) {
      e.stopPropagation();
      setAreSubLinksOpen(false);
    }
  };

  return hasPermission ? (
    <ListGroupItem className="mb-0">
      {hasSubLinks ? (
        <>
          <button
            onClick={handleTopItemClick}
            className={joinClasses(
              "button-no-styles w-100",
              topLinkClasses,
              styles.sidebarItem,
              isSubPageActive ? styles.active : ""
            )}
          >
            {icon && (
              <div className={joinClasses(styles.sidebarIconWrapper, "pe-3")}>
                {icon}
              </div>
            )}
            <div className="flex-grow-1">{text}</div>
            {areSubLinksOpen ? <FiChevronDown /> : <FiChevronRight />}
          </button>
          {areSubLinksOpen && (
            <ul className="list-style-none ps-0 mb-0">
              {subItems.map((item, index) => (
                <SubMenuItem
                  key={(item.linkTo ?? item.title ?? index) + item.text}
                  {...item}
                />
              ))}
            </ul>
          )}
        </>
      ) : (
        <Link
          to={linkTo}
          params={params}
          className={joinClasses(
            "text-decoration-none",
            styles.sidebarItem,
            topLinkClasses
          )}
          onClick={handleLinkClick}
          {...activeLinkOptions}
        >
          {icon && (
            <div className={joinClasses(styles.sidebarIconWrapper, "pe-3")}>
              {icon}
            </div>
          )}
          <div className="flex-grow-1">{text}</div>
        </Link>
      )}
    </ListGroupItem>
  ) : null;
}

export function SubMenuItem({
  text,
  linkTo,
  title,
  isExternal = false,
  onClick,
  hasPermission = true,
}: SubMenuItemProps) {
  const params = useParams({ from: "/$leaid" });

  if (!hasPermission) return null;

  // If no linkTo, render a button instead of a Link to avoid false active states
  if (!linkTo) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          title={title}
          className={joinClasses(
            "button-no-styles text-decoration-none d-block py-2 w-100 text-start",
            styles.sidebarItem,
            styles.sidebarSubItem
          )}
        >
          {text}
        </button>
      </li>
    );
  }

  return (
    <li key={linkTo + text}>
      <Link
        to={linkTo}
        params={params}
        className={joinClasses(
          "text-decoration-none d-block py-2",
          styles.sidebarItem,
          styles.sidebarSubItem
        )}
        title={title}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        onClick={onClick}
        {...activeLinkOptions}
      >
        {text}{" "}
        {isExternal && (
          <span
            aria-hidden="true"
            style={{
              fontSize: "0.85em",
              lineHeight: "1",
              verticalAlign: ".2em",
            }}
          >
            <FiExternalLink className="ms-1" focusable="false" />
          </span>
        )}
      </Link>
    </li>
  );
}
